import { type TeamMemberKind } from '@/features/iam/teams/TeamMembersViewModel'
import { ContentLayout, ContentMain } from '@/components/ContentLayout'
import { ListHeader } from '@/components/ListHeader'
import type { ReactNode } from 'react'
import styles from './TeamMembers.module.css'
import { Dropdown, DropdownItem, DropdownTitle } from '@/components/Dropdown'
import { Icon } from '@/components/Icon'
import { RawButton } from '@/components/Button'
import { match } from '@/utils/pattern-match-util'

export interface TeamMembersLayoutProps {
  children?: ReactNode
  dialogs?: ReactNode
}

export function TeamMembersLayout({
  children,
  dialogs,
}: TeamMembersLayoutProps) {
  return (
    <ContentLayout accountForLabel accountForTabBar>
      <ContentMain>{children}</ContentMain>

      {dialogs}
    </ContentLayout>
  )
}

export interface TeamMembersProps {
  header?: ReactNode
  searchBar?: ReactNode
  table?: ReactNode
}

export function TeamMembers({ header, searchBar, table }: TeamMembersProps) {
  return (
    <div className={styles.root}>
      <div className={styles.headerContainer}>{header}</div>
      <div className={styles.box}>
        <div className={styles.actions}>{searchBar}</div>
        <div className={styles.table}>{table}</div>
      </div>
    </div>
  )
}

export interface MembersListHeaderProps {
  visibleKind: TeamMemberKind
  currentCount: number
  pendingCount: number
  isDropdown: boolean
  onVisibleKindChange: (kind: TeamMemberKind) => void
}

export function MembersListHeader({
  visibleKind,
  currentCount,
  pendingCount,
  isDropdown,
  onVisibleKindChange,
}: MembersListHeaderProps) {
  if (!isDropdown) {
    return (
      <MemberListHeaderLabel
        visibleKind={visibleKind}
        currentCount={currentCount}
        pendingCount={pendingCount}
      />
    )
  }

  return (
    <Dropdown
      autoDismiss={true}
      button={
        <RawButton>
          <MemberListHeaderLabel
            visibleKind={visibleKind}
            currentCount={currentCount}
            pendingCount={pendingCount}
            clickable
            icon={<Icon name="arrow" rotate={90} />}
          />
        </RawButton>
      }
    >
      <DropdownTitle>I want to view...</DropdownTitle>
      <DropdownItem
        active={visibleKind === 'Current'}
        checkmark={visibleKind === 'Current'}
        onPress={() => onVisibleKindChange('Current')}
      >
        Current Members
      </DropdownItem>
      <DropdownItem
        active={visibleKind === 'Pending'}
        checkmark={visibleKind === 'Pending'}
        onPress={() => onVisibleKindChange('Pending')}
      >
        Pending Acceptance
      </DropdownItem>
    </Dropdown>
  )
}

interface MemberListHeaderLabelProps {
  visibleKind: TeamMemberKind
  currentCount: number
  pendingCount: number
  clickable?: boolean
  icon?: ReactNode
}

function MemberListHeaderLabel({
  visibleKind,
  currentCount,
  pendingCount,
  clickable,
  icon,
}: MemberListHeaderLabelProps) {
  return match(visibleKind, {
    Current: () => (
      <ListHeader clickable={clickable} iconRight={icon} count={currentCount}>
        Team Members
      </ListHeader>
    ),
    Pending: () => (
      <ListHeader clickable={clickable} count={pendingCount} iconRight={icon}>
        Pending Members
      </ListHeader>
    ),
  })
}
